import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import {
  GlobeIcon,
  PlusIcon,
  ReloadIcon,
  CheckCircledIcon,
  CrossCircledIcon,
  TrashIcon,
  GearIcon,
} from '@radix-ui/react-icons';
import config from '../config';

function ColorPicker({ label, value, onChange }) {
  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <p style={{ fontSize: '11px', color: '#a1a1aa', marginBottom: '6px', fontWeight: 500 }}>{label}</p>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', height: '36px' }}>
        <label style={{ flexShrink: 0, cursor: 'pointer', position: 'relative', width: '36px', height: '36px' }}>
          <span style={{
            display: 'block',
            width: '36px',
            height: '36px',
            borderRadius: '8px',
            background: value,
            border: '2px solid rgba(255,255,255,0.15)',
          }} />
          <input
            type="color"
            value={value}
            onChange={e => onChange(e.target.value)}
            style={{
              position: 'absolute', inset: 0, opacity: 0,
              width: '100%', height: '100%', cursor: 'pointer', border: 'none', padding: 0,
            }}
          />
        </label>
        <input
          type="text"
          value={value}
          onChange={e => onChange(e.target.value)}
          maxLength={7}
          style={{
            flex: 1, minWidth: 0, height: '36px',
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px',
            padding: '0 10px', fontSize: '13px', color: '#fff',
            fontFamily: 'monospace', outline: 'none', boxSizing: 'border-box',
          }}
        />
      </div>
    </div>
  );
}

function Toggle({ enabled, onToggle }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        width: '44px',
        height: '24px',
        borderRadius: '9999px',
        border: 'none',
        cursor: 'pointer',
        padding: 0,
        flexShrink: 0,
        transition: 'background 0.2s',
        background: enabled ? '#7c3aed' : '#3f3f46',
        outline: 'none',
      }}
    >
      <span style={{
        display: 'inline-block',
        width: '18px',
        height: '18px',
        borderRadius: '9999px',
        background: '#fff',
        boxShadow: '0 1px 3px rgba(0,0,0,0.4)',
        transition: 'transform 0.2s',
        transform: enabled ? 'translateX(22px)' : 'translateX(3px)',
      }} />
    </button>
  );
}

function WordPressSites({ user }) {
  const [sites, setSites] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [testingId, setTestingId] = useState(null);
  const [toast, setToast] = useState(null);
  const [editingSite, setEditingSite] = useState(null); // site being edited
  const [editData, setEditData] = useState({});
  const [editLoading, setEditLoading] = useState(false);
  const [showApiGuide, setShowApiGuide] = useState(false);
  const [autoTestStatus, setAutoTestStatus] = useState(null); // null | 'testing' | 'success' | 'failed'
  const autoTestTimerRef = useRef(null);
  const [formData, setFormData] = useState({
    site_name: '',
    site_url: '',
    api_key: '',
    amazon_tag: '',
    heading_style_enabled: false,
    heading_h2_color: '#6366f1',
    heading_h3_color: '#555555',
    content_spacing_enabled: false,
  });

  const decodeHtml = (str) => {
    const el = document.createElement('textarea');
    el.innerHTML = str;
    return el.value;
  };

  useEffect(() => {
    loadWordPressSites();
  }, []);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  const loadWordPressSites = async () => {
    setInitialLoading(true);
    try {
      const response = await fetch(`${config.apiUrl}/wordpress-sites`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.success) setSites(data.sites);
    } catch (error) {
      showToast('Failed to load WordPress sites', 'error');
    } finally {
      setInitialLoading(false);
    }
  };

  const triggerAutoTest = (url, key) => {
    clearTimeout(autoTestTimerRef.current);
    if (!url || !key) { setAutoTestStatus(null); return; }
    setAutoTestStatus('testing');
    autoTestTimerRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`${config.apiUrl}/wordpress-sites/test-connection`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ site_url: url, api_key: key }),
        });
        const data = await res.json();
        setAutoTestStatus(data.success ? 'success' : 'failed');
      } catch {
        setAutoTestStatus('failed');
      }
    }, 1500);
  };

  const handleAddSite = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await fetch(`${config.apiUrl}/wordpress-sites`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      const data = await response.json();
      if (data.success) {
        setSites([...sites, data.site]);
        setShowAddForm(false);
        setFormData({ site_name: '', site_url: '', api_key: '', amazon_tag: '', heading_style_enabled: false, heading_h2_color: '#6366f1', heading_h3_color: '#555555', content_spacing_enabled: false });
        showToast('WordPress site added successfully!');
      } else {
        showToast('Failed to add site: ' + data.message, 'error');
      }
    } catch (error) {
      showToast('Failed to add WordPress site', 'error');
    } finally {
      setLoading(false);
    }
  };

  const testConnection = async (site) => {
    setTestingId(site.id);
    try {
      const response = await fetch(`${config.apiUrl}/wordpress-sites/${site.id}/test`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.success) {
        const name = decodeHtml(data.site_info?.site_name || site.site_name);
        showToast(`✅ Connected to "${name}"`);
      } else {
        showToast('Connection failed: ' + data.message, 'error');
      }
    } catch {
      showToast('Connection test failed', 'error');
    } finally {
      setTestingId(null);
    }
  };

  const confirmDelete = async () => {
    try {
      const response = await fetch(`${config.apiUrl}/wordpress-sites/${deleteConfirm}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      if (response.ok) {
        setSites(sites.filter(s => s.id !== deleteConfirm));
        showToast('WordPress site removed');
      } else {
        showToast('Failed to remove site', 'error');
      }
    } catch {
      showToast('Failed to remove site', 'error');
    } finally {
      setDeleteConfirm(null);
    }
  };

  const openEdit = (site) => {
    setEditingSite(site.id);
    setEditData({
      heading_style_enabled: !!site.heading_style_enabled,
      heading_h2_color: site.heading_h2_color || '#6366f1',
      heading_h3_color: site.heading_h3_color || '#555555',
      content_spacing_enabled: !!site.content_spacing_enabled,
      generation_defaults: site.generation_defaults || {},
    });
  };

  const saveEdit = async () => {
    setEditLoading(true);
    try {
      const response = await fetch(`${config.apiUrl}/wordpress-sites/${editingSite}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(editData),
      });
      const data = await response.json();
      if (data.success) {
        setSites(sites.map(s => s.id === editingSite ? { ...s, ...editData } : s));
        setEditingSite(null);
        showToast('Site settings saved!');
      } else {
        showToast('Failed to save: ' + (data.message || 'Unknown error'), 'error');
      }
    } catch {
      showToast('Failed to save settings', 'error');
    } finally {
      setEditLoading(false);
    }
  };

  const inputClass = "w-full h-10 bg-white/5 border border-white/10 rounded-lg px-3 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-violet-500/50 transition-colors";
  const labelClass = "block text-xs font-medium text-zinc-400 mb-1.5";

  return (
    <div className="text-zinc-50">

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-5 py-3 rounded-xl border shadow-xl text-sm font-medium transition-all animate-fade-in-up whitespace-nowrap ${toast.type === 'error'
          ? 'bg-red-500/10 border-red-500/30 text-red-300'
          : 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300'
          }`}>
          {toast.type === 'error'
            ? <CrossCircledIcon className="w-4 h-4 shrink-0" />
            : <CheckCircledIcon className="w-4 h-4 shrink-0" />}
          {toast.message}
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 p-8 overflow-y-auto custom-scrollbar">
          <div className="max-w-4xl mx-auto space-y-6">

            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-white via-zinc-200 to-zinc-400 bg-clip-text text-transparent flex items-center gap-2">
                  🌐 WordPress Sites
                </h1>
                <p className="text-sm text-zinc-500 mt-1">Manage your connected WordPress sites for publishing</p>
              </div>
              <Button
                onClick={() => setShowAddForm(true)}
                className="bg-white text-zinc-950 hover:bg-zinc-200 font-semibold text-xs h-9 px-4 rounded-xl shadow-md shadow-white/5 flex items-center gap-2"
              >
                <PlusIcon className="w-4 h-4" />
                Add Site
              </Button>
            </div>

            {/* Add Site Form */}
            {showAddForm && (
              <div className="bg-zinc-900/60 backdrop-blur-xl border border-white/10 rounded-2xl p-6 shadow-xl">
                <div className="flex items-center justify-between mb-5">
                  <h2 className="text-base font-semibold text-white flex items-center gap-2">
                    <PlusIcon className="w-4 h-4 text-violet-400" />
                    Add WordPress Site
                  </h2>
                  <button
                    onClick={() => { setShowAddForm(false); setFormData({ site_name: '', site_url: '', api_key: '', amazon_tag: '', heading_style_enabled: false, heading_h2_color: '#6366f1', heading_h3_color: '#555555', content_spacing_enabled: false }); }}
                    className="text-zinc-500 hover:text-zinc-200 transition-colors text-lg leading-none"
                  >×</button>
                </div>

                <form onSubmit={handleAddSite} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={labelClass}>Site Name</label>
                      <input
                        type="text"
                        className={inputClass}
                        placeholder="My Blog"
                        value={formData.site_name}
                        onChange={e => setFormData({ ...formData, site_name: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <label className={labelClass}>Site URL</label>
                      <input
                        type="url"
                        className={inputClass}
                        placeholder="https://yourblog.com"
                        value={formData.site_url}
                        onChange={e => {
                          const url = e.target.value;
                          setFormData({ ...formData, site_url: url });
                          triggerAutoTest(url, formData.api_key);
                        }}
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className={labelClass} style={{ marginBottom: 0 }}>API Key</label>
                      <button
                        type="button"
                        onClick={() => setShowApiGuide(g => !g)}
                        className="text-xs text-violet-400 hover:text-violet-300 transition-colors"
                      >
                        {showApiGuide ? '▲ Hide guide' : '▼ How to get your API key?'}
                      </button>
                    </div>
                    {showApiGuide && (
                      <div className="mb-2 p-3 rounded-lg bg-violet-500/10 border border-violet-500/20 text-xs text-zinc-300 space-y-1.5">
                        <p className="font-semibold text-violet-300">3 steps to get your API key:</p>
                        <p>1. In WordPress admin, go to <span className="text-white font-medium">Settings → ContentVertex Bridge</span></p>
                        <p>2. Copy the <span className="text-white font-medium">API Key</span> shown there (starts with <span className="font-mono text-violet-300">cv_</span>)</p>
                        <p>3. Paste it in the field below</p>
                      </div>
                    )}
                    <input
                      type="text"
                      className={inputClass}
                      placeholder="cv_xxxxxxxxxxxxxxxxx"
                      value={formData.api_key}
                      onChange={e => {
                        const key = e.target.value;
                        setFormData({ ...formData, api_key: key });
                        triggerAutoTest(formData.site_url, key);
                      }}
                      required
                    />
                    <div className="flex items-center gap-2 mt-1.5">
                      {autoTestStatus === 'testing' && (
                        <span className="text-xs text-zinc-400 flex items-center gap-1.5">
                          <ReloadIcon className="h-3 w-3 animate-spin text-violet-400" /> Testing connection…
                        </span>
                      )}
                      {autoTestStatus === 'success' && (
                        <span className="text-xs text-emerald-400 flex items-center gap-1.5">
                          <CheckCircledIcon className="h-3 w-3" /> Connected successfully
                        </span>
                      )}
                      {autoTestStatus === 'failed' && (
                        <span className="text-xs text-red-400 flex items-center gap-1.5">
                          <CrossCircledIcon className="h-3 w-3" /> Connection failed — check URL and key
                        </span>
                      )}
                      {!autoTestStatus && (
                        <p className="text-xs text-zinc-500">Get this from <span className="text-violet-400">Settings → ContentVertex Bridge</span> in your WordPress admin</p>
                      )}
                    </div>
                  </div>


                  {/* Heading Style */}
                  <div className="border border-white/10 rounded-xl p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-white">Article Heading Style</p>
                        <p className="text-xs text-zinc-500 mt-0.5">Add a colored left border to H2 headings and style H3 headings</p>
                      </div>
                      <Toggle enabled={formData.heading_style_enabled} onToggle={() => setFormData({ ...formData, heading_style_enabled: !formData.heading_style_enabled })} />
                    </div>

                    {formData.heading_style_enabled && (
                      <div className="space-y-3 pt-1">
                        <div style={{ display: 'flex', gap: '12px' }}>
                          <ColorPicker label="H2 Border Color" value={formData.heading_h2_color} onChange={v => setFormData({ ...formData, heading_h2_color: v })} />
                          <ColorPicker label="H3 Text Color" value={formData.heading_h3_color} onChange={v => setFormData({ ...formData, heading_h3_color: v })} />
                        </div>
                        <div>
                          <p className="text-xs text-zinc-600 mb-1">Preview:</p>
                          <div className="bg-white/5 rounded-lg p-3 space-y-1">
                            <div style={{ borderLeft: `4px solid ${formData.heading_h2_color}`, paddingLeft: '10px', fontWeight: 700, fontSize: '14px', color: '#fff' }}>Section Heading (H2)</div>
                            <div style={{ paddingLeft: '14px', fontStyle: 'italic', fontSize: '12px', color: formData.heading_h3_color }}>Sub-section Heading (H3)</div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Content Spacing */}
                  <div className="border border-white/10 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-white">Article Content Spacing</p>
                        <p className="text-xs text-zinc-500 mt-0.5">Add breathing room between paragraphs, lists, and sections</p>
                      </div>
                      <Toggle enabled={formData.content_spacing_enabled} onToggle={() => setFormData({ ...formData, content_spacing_enabled: !formData.content_spacing_enabled })} />
                    </div>
                  </div>

                  <div className="flex gap-3 pt-1">
                    <button
                      type="button"
                      onClick={() => setShowAddForm(false)}
                      className="flex-1 h-10 rounded-xl border border-white/10 text-zinc-400 hover:text-white hover:bg-white/5 text-sm transition-all"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="flex-1 h-10 rounded-xl bg-white text-zinc-950 font-semibold text-sm hover:bg-zinc-200 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {loading ? <><ReloadIcon className="w-4 h-4 animate-spin" /> Adding...</> : 'Add Site'}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Sites List */}
            {initialLoading ? (
              <div className="flex items-center justify-center py-24">
                <div className="relative">
                  <div className="w-12 h-12 border-4 border-white/5 rounded-full" />
                  <div className="absolute top-0 left-0 w-12 h-12 border-4 border-t-violet-500 rounded-full animate-spin" />
                </div>
              </div>
            ) : sites.length === 0 ? (
              <div className="bg-zinc-900/40 backdrop-blur-xl border border-white/10 rounded-2xl p-16 text-center">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-500/20 to-purple-500/20 flex items-center justify-center mx-auto mb-4">
                  <GlobeIcon className="w-8 h-8 text-violet-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">No sites connected yet</h3>
                <p className="text-sm text-zinc-500 max-w-md mx-auto mb-6">
                  Add your first WordPress site to start publishing articles directly from ContentVertex.
                </p>
                <Button
                  onClick={() => setShowAddForm(true)}
                  className="bg-white text-zinc-950 hover:bg-zinc-200 font-semibold text-xs h-9 px-5 rounded-xl"
                >
                  <PlusIcon className="w-4 h-4 mr-2" />
                  Add Your First Site
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {sites.map(site => (
                  <div key={site.id} className="group bg-zinc-900/40 backdrop-blur-xl border border-white/10 hover:border-white/20 rounded-2xl p-5 transition-all duration-200 hover:shadow-lg hover:shadow-black/30">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-4 min-w-0">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500/20 to-blue-500/20 border border-white/10 flex items-center justify-center shrink-0">
                          <GlobeIcon className="w-5 h-5 text-violet-400" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-white text-sm truncate">{site.site_name}</h3>
                            <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border ${site.status === 'connected'
                              ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400'
                              : 'bg-zinc-500/10 border-zinc-500/30 text-zinc-400'
                              }`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${site.status === 'connected' ? 'bg-emerald-400' : 'bg-zinc-500'}`} />
                              {site.status || 'unknown'}
                            </span>
                          </div>
                          <p className="text-xs text-zinc-500 truncate mt-0.5">{site.site_url}</p>
                          <p className="text-xs text-zinc-600 mt-0.5">Added {new Date(site.created_at).toLocaleDateString()}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          onClick={() => testConnection(site)}
                          disabled={testingId === site.id}
                          className="flex items-center gap-1.5 px-3 h-8 rounded-lg border border-white/10 text-zinc-400 hover:text-white hover:bg-white/5 text-xs transition-all disabled:opacity-50"
                        >
                          {testingId === site.id
                            ? <><ReloadIcon className="w-3 h-3 animate-spin" /> Testing...</>
                            : <><CheckCircledIcon className="w-3 h-3" /> Test</>}
                        </button>
                        <button
                          onClick={() => openEdit(site)}
                          className="flex items-center gap-1.5 px-3 h-8 rounded-lg border border-white/10 text-zinc-400 hover:text-white hover:bg-white/5 text-xs transition-all"
                        >
                          <GearIcon className="w-3 h-3" />
                          Settings
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(site.id)}
                          className="flex items-center gap-1.5 px-3 h-8 rounded-lg border border-red-500/20 text-red-400/60 hover:text-red-400 hover:bg-red-500/10 text-xs transition-all"
                        >
                          <TrashIcon className="w-3 h-3" />
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
      </main>

      {/* Edit Site Settings Modal */}
      {editingSite && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-zinc-900 border border-white/[0.07] rounded-2xl p-6 w-full max-w-lg mx-4 shadow-2xl max-h-[90vh] overflow-y-auto custom-scrollbar">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-base font-semibold text-white flex items-center gap-2">
                <GearIcon className="w-4 h-4 text-violet-400" />
                Site Settings
              </h3>
              <button onClick={() => setEditingSite(null)} className="text-zinc-500 hover:text-zinc-200 transition-colors text-lg leading-none">×</button>
            </div>

            <div className="border border-white/10 rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-white">Article Heading Style</p>
                  <p className="text-xs text-zinc-500 mt-0.5">Colored left border on H2, styled H3</p>
                </div>
                <Toggle enabled={editData.heading_style_enabled} onToggle={() => setEditData({ ...editData, heading_style_enabled: !editData.heading_style_enabled })} />
              </div>

              {editData.heading_style_enabled && (
                <div className="space-y-3 pt-1">
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <ColorPicker label="H2 Border Color" value={editData.heading_h2_color} onChange={v => setEditData({ ...editData, heading_h2_color: v })} />
                    <ColorPicker label="H3 Text Color" value={editData.heading_h3_color} onChange={v => setEditData({ ...editData, heading_h3_color: v })} />
                  </div>
                  <div>
                    <p className="text-xs text-zinc-600 mb-1">Preview:</p>
                    <div className="bg-white/5 rounded-lg p-3 space-y-1">
                      <div style={{ borderLeft: `4px solid ${editData.heading_h2_color}`, paddingLeft: '10px', fontWeight: 700, fontSize: '14px', color: '#fff' }}>Section Heading (H2)</div>
                      <div style={{ paddingLeft: '14px', fontStyle: 'italic', fontSize: '12px', color: editData.heading_h3_color }}>Sub-section Heading (H3)</div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Content Spacing */}
            <div className="border border-white/10 rounded-xl p-4 mt-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-white">Article Content Spacing</p>
                  <p className="text-xs text-zinc-500 mt-0.5">Add breathing room between paragraphs, lists, and sections</p>
                </div>
                <Toggle enabled={editData.content_spacing_enabled} onToggle={() => setEditData({ ...editData, content_spacing_enabled: !editData.content_spacing_enabled })} />
              </div>
            </div>

            {/* Generation Defaults */}
            <div className="mt-4 border-t border-white/10 pt-4 space-y-3">
              <p className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Generation Defaults for this site</p>

              <div>
                <label className="text-xs text-zinc-400 mb-1 block">AI Model</label>
                <select
                  className="w-full h-9 bg-white/5 border border-white/10 rounded-lg px-3 text-sm text-white focus:outline-none"
                  value={editData.generation_defaults?.ai_model || ''}
                  onChange={e => setEditData({ ...editData, generation_defaults: { ...(editData.generation_defaults || {}), ai_model: e.target.value || undefined } })}
                >
                  <option value="">Use global default</option>
                  <optgroup label="Gemini">
                    <option value="gemini-2.5-flash-lite">⚡ Gemini 2.5 Flash Lite</option>
                    <option value="gemini-2.5-flash">🔥 Gemini 2.5 Flash</option>
                    <option value="gemini-2.5-pro">🌟 Gemini 2.5 Pro</option>
                  </optgroup>
                  <optgroup label="ChatGPT">
                    <option value="gpt-4o-mini">💰 GPT-4o Mini</option>
                    <option value="gpt-4.1-mini">⚡ GPT-4.1 Mini</option>
                  </optgroup>
                  <optgroup label="Claude">
                    <option value="claude-haiku-4-5-20251001">⚡ Claude Haiku 4.5</option>
                    <option value="claude-sonnet-4-6">✍️ Claude Sonnet 4.6</option>
                  </optgroup>
                </select>
              </div>

              <div>
                <label className="text-xs text-zinc-400 mb-1 block">Article Length</label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
                  {[1200, 1600, 2000, 2500].map(len => (
                    <button
                      key={len}
                      type="button"
                      onClick={() => setEditData({ ...editData, generation_defaults: { ...(editData.generation_defaults || {}), article_length: len } })}
                      className={`py-1.5 rounded-lg border text-xs font-medium transition-all ${editData.generation_defaults?.article_length === len ? 'border-violet-500/60 bg-violet-500/10 text-violet-300' : 'border-white/10 bg-white/5 text-zinc-400 hover:border-white/20'}`}
                    >
                      {len}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs text-zinc-400 mb-1 block">Publish Type</label>
                <div className="flex gap-2">
                  {[{ value: 'draft', label: 'Draft' }, { value: 'publish', label: 'Publish' }].map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setEditData({ ...editData, generation_defaults: { ...(editData.generation_defaults || {}), publish_type: opt.value } })}
                      className={`flex-1 py-1.5 rounded-lg border text-xs font-medium transition-all ${editData.generation_defaults?.publish_type === opt.value ? 'border-violet-500/60 bg-violet-500/10 text-violet-300' : 'border-white/10 bg-white/5 text-zinc-400 hover:border-white/20'}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-4">
              <button onClick={() => setEditingSite(null)} className="flex-1 h-10 rounded-xl border border-white/10 text-zinc-400 hover:text-white hover:bg-white/5 text-sm transition-all">Cancel</button>
              <button onClick={saveEdit} disabled={editLoading} className="flex-1 h-10 rounded-xl bg-white text-zinc-950 font-semibold text-sm hover:bg-zinc-200 transition-all disabled:opacity-50 flex items-center justify-center gap-2">
                {editLoading ? <><ReloadIcon className="w-4 h-4 animate-spin" /> Saving...</> : 'Save Settings'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-zinc-900 border border-white/10 rounded-2xl p-6 w-80 shadow-2xl">
            <h3 className="text-base font-semibold text-white mb-2">Remove Site?</h3>
            <p className="text-sm text-zinc-400 mb-5">This will disconnect the WordPress site. Published articles will remain unaffected.</p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 h-10 rounded-xl border border-white/10 text-zinc-400 hover:text-white hover:bg-white/5 text-sm transition-all"
              >Cancel</button>
              <button
                onClick={confirmDelete}
                className="flex-1 h-10 rounded-xl bg-red-500/20 border border-red-500/30 text-red-400 hover:bg-red-500/30 text-sm font-medium transition-all"
              >Remove</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default WordPressSites;